import moment, { Moment } from 'moment-timezone';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import styles from './CancellationNoShows.module.scss';

import Header from '../../components/Header';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Period } from '@spike/model';
import { useTimeZone } from 'hooks';

import CancellationNoShowsTable from './CancellationNoShowsTable';
import { getCancellationAndNoShowsReport } from 'actions/insights/bookings/cancellationAndNoShows/CancellationAndNoShowsReportActions';
import ToggleTabs from 'components/Insights/components/ToogleTabs/ToggleTabs';
import CancellationNoShowsChart from './CancellationNoShowsChart';

interface CancellationNoShowsProps {
    description?: string;
}

const options = [
    { id: 'table', label: 'Table' },
    { id: 'chart', label: 'Chart' }
];

const toDateParam = (m: Moment) => m.format('YYYY-MM-DD');

const pct = (v: any) => ((Number(v ?? 0) * 100).toFixed(2) + '%') as string;

export const CancellationNoShows: FunctionComponent<CancellationNoShowsProps> = props => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();
    const [currentTab, setCurrentTab] = useState('table');

    const { report, loading } = useSelector((s: RootState) => s.cancellationAndNoShowsReport);

    const defaultPeriod: Period = useMemo(() => {
        const now = moment().tz(timeZone);
        return {
            from: now.clone().startOf('day'),
            to: now.clone().endOf('day')
        };
    }, [timeZone]);

    const [period, setPeriod] = useState<Period | null>(defaultPeriod);

    useEffect(() => {
        if (!period) {
            dispatch(getCancellationAndNoShowsReport());
            return;
        }
        dispatch(getCancellationAndNoShowsReport(toDateParam(period.from), toDateParam(period.to)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calendar = { optionSelected: 'Today' };

    const handleChangePeriod = (p: Period | null, allTime: boolean) => {
        if (allTime || !p) {
            setPeriod(null);
            dispatch(getCancellationAndNoShowsReport());
            return;
        }

        const from = p.from.clone().tz(timeZone);
        const to = p.to.clone().tz(timeZone);

        setPeriod({ from, to });
        dispatch(getCancellationAndNoShowsReport(toDateParam(from), toDateParam(to)));
    };

    const fmtCurrency = (n: number) =>
        Number(n ?? 0).toLocaleString(undefined, {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        });

    const fmtPct = (ratio: number) => `${Math.round(Number(ratio ?? 0) * 100)}%`;

    const statusLabel = (statusId: number | string) => {
        if (statusId === 'total') return 'Total';

        switch (statusId) {
            case 1:
                return 'Mistake or Error';
            case 2:
                return 'Staff canceled';
            case 3:
                return 'Client canceled';
            case 4:
                return 'No Show';
            case 5:
                return 'Pet Decline';
            case 'no_reason':
                return 'No reason';
            default:
                return `Status ${statusId}`;
        }
    };

    const exportFile = useMemo(() => {
        const rows = report?.rows ?? [];

        return {
            name: 'Cancellation & No Shows.csv',
            dataInMemory: true,
            headers: [
                'Status',
                'Total Bookings',
                'Total Value',
                'Deposit Collected',
                'Rebooked',
                'Not Rebooked',
                'Missed Revenue'
            ],
            dataToExport: rows.map((row: any) => {
                const statusId = row.statusId ?? row.status_id;

                return [
                    statusLabel(statusId),
                    String(row.totalBookings ?? row.total_bookings ?? 0),
                    fmtCurrency(row.totalValue ?? row.total_value ?? 0),
                    fmtCurrency(row.depositCollected ?? row.deposit_collected ?? 0),
                    `${row.rebooked?.value ?? 0} (${fmtPct(row.rebooked?.pct ?? 0)})`,
                    `${row.notRebooked?.value ?? 0} (${fmtPct(row.notRebooked?.pct ?? 0)})`,
                    fmtCurrency(row.missedRevenue ?? row.missed_revenue ?? 0)
                ];
            }) as string[][]
        };
    }, [report]);

    return (
        <div className={styles.container}>
            <div>
                <span className={styles.description}> {props.description} </span>
            </div>

            <div className={styles.categoryContainer}>
                <ToggleTabs
                    options={options}
                    value={currentTab}
                    setValue={setCurrentTab}
                />
            </div>

            <div>
                <Header
                    period={period}
                    onChangePeriod={handleChangePeriod}
                    exportFile={exportFile}
                    calendar={calendar}
                    hideAllTime={true}
                />
            </div>

            {currentTab === 'table' && (
                <div className={styles.tableContainer}>
                    <CancellationNoShowsTable
                        loading={loading}
                        report={report}
                    />
                </div>
            )}

            {currentTab === 'chart' && (
                <div className={styles.chartContainer}>
                    <CancellationNoShowsChart
                        loading={loading}
                        report={report}
                    />
                </div>
            )}
        </div>
    );
};

export default CancellationNoShows;
